import type { ApiResult, ApiSuccess, GenericError } from '@/core/either';
import { left, right } from '@/core/either';
import { logger } from '@/shared/common/utils/logger.util';

/**
 * Mapea errores de Firestore a mensajes legibles
 * @param error - Error de Firestore
 * @returns Mensaje de error legible
 */
const getFirestoreErrorMessage = (error: any): string => {
  const code = error?.code || 'UNKNOWN';

  switch (code) {
    case 'permission-denied':
      return 'No tienes permisos para acceder a estos datos';
    case 'unavailable':
      return 'Servicio no disponible. Intenta de nuevo más tarde';
    case 'unauthenticated':
      return 'Debes autenticarte para acceder a estos datos';
    case 'not-found':
      return 'Recurso no encontrado';
    case 'already-exists':
      return 'El recurso ya existe';
    case 'failed-precondition':
      return 'La operación falló debido a una condición previa';
    case 'aborted':
      return 'La operación fue cancelada';
    case 'out-of-range':
      return 'Valor fuera del rango permitido';
    case 'unimplemented':
      return 'Operación no implementada';
    case 'internal':
      return 'Error interno del servidor';
    case 'deadline-exceeded':
      return 'Tiempo de espera agotado';
    case 'resource-exhausted':
      return 'Recursos agotados. Intenta más tarde';
    default:
      return error?.message || 'Error al realizar la operación';
  }
};

/**
 * Convierte un error de Firestore a GenericError
 * @param error - Error de Firestore
 * @returns GenericError compatible con ApiFailure
 */
const handleFirestoreError = (error: any): GenericError => {
  const code = error?.code || 'UNKNOWN';
  const status = error?.status || 500;
  const message = getFirestoreErrorMessage(error);

  const genericError: GenericError = {
    code,
    reason: 'FIRESTORE_ERROR',
    status,
    message,
  };

  logger.error('Firestore Error', { error, code, message }, error as Error);

  return genericError;
};

/**
 * Ejecuta una operación de Firestore y maneja automáticamente los errores con Either
 * @param requestFn - Función que retorna una promesa de Firestore
 * @returns Promise con Either<ApiFailure, ApiSuccess<T>>
 */
export const executeFirestoreRequest = async <T>(
  requestFn: () => Promise<T>
): Promise<ApiResult<T>> => {
  try {
    const data = await requestFn();
    const success: ApiSuccess<T> = {
      data,
      status: 200,
    };
    return right(success);
  } catch (error) {
    const genericError = handleFirestoreError(error);
    return left(genericError);
  }
};

